import { Button, Group, Stack } from '@forwardflow/ui-v2'
import OrganizationService from '@forwardflow/services/OrganizationService'
import { useAppStore } from '@forwardflow/store/appStore'
import { IAriaAgent } from '@forwardflow/types/automations'
import { Loader, Text } from '@mantine/core'
import { message } from 'antd'
import { useEffect, useState } from 'react'

interface SayHiSectionProps {
   /** Recipient emails selected in the form — drives the eligible-agent list. */
   emails: string[]
}

const SayHiSection = ({ emails }: SayHiSectionProps) => {
   const orgId = useAppStore((s) => s.currentOrganization)
   const [agents, setAgents] = useState<IAriaAgent[]>([])
   const [loading, setLoading] = useState(false)
   const [error, setError] = useState<string | null>(null)
   const [sendingId, setSendingId] = useState<string | null>(null)

   const emailsKey = emails.join(',')

   useEffect(() => {
      if (!emails.length) {
         setAgents([])
         return
      }
      let cancelled = false
      setLoading(true)
      setError(null)
      OrganizationService.getEligibleAriaAgents(emails)
         .then((list) => {
            if (!cancelled) setAgents(list)
         })
         .catch((e) => {
            if (cancelled) return
            console.error('Failed to load eligible agents:', e)
            setError('Failed to load agents')
            setAgents([])
         })
         .finally(() => {
            if (!cancelled) setLoading(false)
         })
      return () => {
         cancelled = true
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [emailsKey])

   const handleSayHi = async (agent: IAriaAgent) => {
      if (!orgId) {
         message.error('No organization selected')
         return
      }
      // conversationId is `${orgId}:${email}` — the backend filters by the bare
      // email, so strip the org prefix (works too if it's already an email).
      const colonIdx = agent.conversationId.indexOf(':')
      const agentEmail =
         colonIdx >= 0
            ? agent.conversationId.slice(colonIdx + 1)
            : agent.conversationId
      setSendingId(agent.conversationId)
      try {
         await OrganizationService.sayHiFromAria(orgId, agentEmail)
         message.success(`Aria said hi to ${agent.agentName} 👋`)
      } catch (e) {
         console.error('Say hi failed:', e)
         message.error('Failed to say hi')
      } finally {
         setSendingId(null)
      }
   }

   return (
      <div className='rounded-lg border border-white/10 bg-white/[0.02] p-5'>
         <Stack gap={4}>
            <Text className='text-white text-sm font-medium'>Say hi from Aria</Text>
            <Text className='text-white/50 text-xs'>
               Have Aria introduce herself to an agent to start the conversation.
               Agents are based on the selected recipients above.
            </Text>
         </Stack>

         <div className='mt-4'>
            {!emails.length ? (
               <Text className='text-white/40 text-xs'>
                  Select at least one recipient to see eligible agents.
               </Text>
            ) : loading ? (
               <Group className='py-3'>
                  <Loader size='sm' />
               </Group>
            ) : error ? (
               <Text className='text-red-400 text-xs'>{error}</Text>
            ) : agents.length === 0 ? (
               <Text className='text-white/40 text-xs'>
                  No eligible agents for the selected recipients.
               </Text>
            ) : (
               <Stack gap={8}>
                  {agents.map((agent) => (
                     <Group
                        key={agent.conversationId}
                        justify='space-between'
                        className='rounded-md border border-white/10 px-3 py-2'
                     >
                        <Stack gap={0}>
                           <Text className='text-white text-sm'>{agent.agentName}</Text>
                           <Text className='text-white/40 text-xs'>
                              {agent.toNumber || agent.conversationId}
                           </Text>
                        </Stack>
                        <Button
                           variant='secondary'
                           className='!h-9 !rounded !mt-0 max-w-32'
                           loading={sendingId === agent.conversationId}
                           disabled={sendingId !== null}
                           onClick={() => handleSayHi(agent)}
                        >
                           Say hi 👋
                        </Button>
                     </Group>
                  ))}
               </Stack>
            )}
         </div>
      </div>
   )
}

export default SayHiSection
